/**
 * @file Pure view helpers for the Settings | Secrets panel — daemon
 *       grouping, secret staleness, and rail counts. No React, no DOM;
 *       unit-tested directly.
 * @module @labcraft/admin/ui/settings/view
 */

import type { SettingsSchemaResponse } from '../../shared/settings_routes.ts'
import type { SecretsListResponse } from '../../shared/secrets_routes.ts'

/** One daemon group in the settings schema. */
export type SettingsGroup = SettingsSchemaResponse['daemons'][number]

/** One declared-secret list entry (metadata only). */
export type SecretEntry = SecretsListResponse['entries'][number]

/** A daemon's secrets, grouped for the Secrets sub-view. */
export type SecretGroup = { daemon: string; entries: SecretEntry[] }

/** Age in ms past which a secret is surfaced for rotation. */
const SECRET_STALE_MS = 90 * 86_400_000

/** Pure derivations over settings schema + secret metadata. */
export class SettingsView {
  /**
   * Filter settings groups to the rail's daemon selection.
   *
   * @param groups - All schema daemon groups.
   * @param daemon - Selected daemon id, or `'all'`.
   * @returns The matching groups.
   */
  static filterGroups(
    groups: SettingsGroup[],
    daemon: string,
  ): SettingsGroup[] {
    if (daemon === 'all') return groups
    return groups.filter(g => g.id === daemon)
  }

  /**
   * Whether a secret needs rotation: never written, or last written
   * more than 90 days ago.
   *
   * @param entry - The secret metadata.
   * @param now - Current time in ms.
   * @returns Whether the secret is stale.
   */
  static isStaleSecret(entry: SecretEntry, now: number): boolean {
    if (entry.version === null || entry.lastRotated === null) return true
    return now - new Date(entry.lastRotated).getTime() > SECRET_STALE_MS
  }

  /**
   * Distinct daemons that own at least one declared secret, sorted.
   *
   * @param entries - All secret entries.
   * @returns Sorted distinct daemon ids.
   */
  static secretDaemons(entries: SecretEntry[]): string[] {
    return [...new Set(entries.map(e => e.definition.daemon))].sort()
  }

  /**
   * Group secrets by daemon for the Secrets sub-view, applying the
   * rail's daemon + "needs rotation" filters.
   *
   * @param entries - All secret entries.
   * @param daemon - Selected daemon id, or `'all'`.
   * @param onlyStale - Restrict to secrets needing rotation.
   * @param now - Current time in ms.
   * @returns Daemon-grouped, filtered secret entries (empty groups dropped).
   */
  static groupSecrets(
    entries: SecretEntry[],
    daemon: string,
    onlyStale: boolean,
    now: number,
  ): SecretGroup[] {
    const filtered = entries.filter(
      e =>
        (daemon === 'all' || e.definition.daemon === daemon) &&
        (!onlyStale || SettingsView.isStaleSecret(e, now)),
    )
    return SettingsView.secretDaemons(filtered).map(d => ({
      daemon: d,
      entries: filtered.filter(e => e.definition.daemon === d),
    }))
  }

  /**
   * Count of secrets needing rotation — drives the rail's
   * "Needs rotation" badge.
   *
   * @param entries - All secret entries.
   * @param now - Current time in ms.
   * @returns The stale count.
   */
  static staleCount(entries: SecretEntry[], now: number): number {
    return entries.filter(e => SettingsView.isStaleSecret(e, now)).length
  }
}
